import React, { useState, useEffect } from "react";
import { FLOW_TYPES, PAYMENTS_MOD } from "./Constants";
import { useCategoryForm } from '../hooks/useCategoryForm'
import { authFetch } from '../utils/api';

export default function FactureModal({
    show,
    editingFacture,
    currentUser,
    categories,
    accounts,
    persons,
    selectedPerson,
    selectedAccount,
    onSave,
    onCancel,
    onDeleteCallback,
    refreshDashboardData,
    refreshAllData,
}) {
    // Liste complète des personnes (gérées et non gérées) + Défaut
    const allPersons = [
        ...persons,
    ].filter((p, index, self) => index === self.findIndex((t) => (
        t.id === p.id && t.name === p.name
    )));

    const [formData, setFormData] = useState({
        date: "",
        categorie_id: "",
        account_id: "",
        person_id: "",
        statut: "Non payé",
        montant: "",
        description: "",

        bill_payments: [],
        bill_beneficiaries: [],
        paiements: [], // Pour la compatibilité
    });

    // Callback pour mettre à jour formData.categorie_id
    const onCategoryCreated = (newCategoryId) => {
        setFormData(prevData => ({ ...prevData, categorie_id: newCategoryId }));
    };
    
    // ✅ NOUVEAU : Appel du hook
    const {
        showCategoryForm,
        newCategoryName,
        newCategoryType,
        setNewCategoryName,
        setNewCategoryType,
        setShowCategoryForm,
        handleCreateCategory,
    } = useCategoryForm(currentUser, onCategoryCreated, refreshAllData);

    const visibleCategories = categories.filter(c => !c.is_hidden);

    // Synchronisation des données (Facture existante ou nouvelle)
    useEffect(() => {
        if (editingFacture) {
            const dateObj = new Date(editingFacture.date);
            const formattedDate = dateObj.toISOString().split("T")[0];

            // ID de la personne principale (utilisé comme fallback pour les anciens paiements)
            const mainPersonId = editingFacture.person_id || selectedPerson?.id || 0;

            // 💡 1. Initialisation Bill Payments
            let initialBillPayments = editingFacture.bill_payments?.map(p => ({
                ...p,
                // Stocker l'ID comme Nombre dans l'état
                payer_person_id: Number(p.payer_person_id),
            })) || [];

            // Correction du fallback
            if (initialBillPayments.length === 0 && editingFacture.paiements) {
                initialBillPayments = editingFacture.paiements.map(p => ({
                    mode: p.mode,
                    montant: p.montant,
                    payer_person_id: Number(mainPersonId),
                }));
            }

            // 💡 2. Initialisation Bill Beneficiaries
            let initialBillBeneficiaries = editingFacture.bill_beneficiaries?.map(b => ({
                ...b,
                beneficiary_person_id: Number(b.beneficiary_person_id),
            })) || [];

            // Correction du fallback
            if (initialBillBeneficiaries.length === 0) {
                initialBillBeneficiaries = [{
                    beneficiary_person_id: Number(mainPersonId),
                    amount_owed: editingFacture.montant,
                }];
            }


            setFormData({
                ...editingFacture,
                date: formattedDate,
                // 💡 CORRECTION : Stocker comme nombre ou ""
                person_id: Number(mainPersonId),
                // 💡 CORRECTION : Stocker comme nombre ou ""
                account_id: Number(editingFacture.account_id),

                bill_payments: initialBillPayments,
                bill_beneficiaries: initialBillBeneficiaries,

                paiements: editingFacture.paiements || [],
            });
        } else {
            // Logique de création (déjà correcte)
            const initialPaiementAmount = "";
            const initialPaiementPerson = selectedPerson?.id || "";

            setFormData({
                date: new Date().toISOString().split("T")[0],
                categorie_id: "",
                // Les IDs sont stockés en nombre (ou "" si non défini)
                account_id: selectedAccount?.id || "",
                person_id: initialPaiementPerson,
                statut: "Payé",
                montant: "",
                description: "",

                bill_payments: [{
                    mode: 'Virement',
                    montant: initialPaiementAmount,
                    payer_person_id: initialPaiementPerson,
                }],
                bill_beneficiaries: [{
                    beneficiary_person_id: initialPaiementPerson,
                    amount_owed: initialPaiementAmount,
                }],

                paiements: [{ mode: 'Virement', montant: initialPaiementAmount }],
            });
        }
    }, [editingFacture, selectedPerson, selectedAccount]);

    const handleMontantChange = (e) => {
        const value = e.target.value;

        setFormData(prevData => {
            // Mise à jour de la valeur principale
            let updatedData = { ...prevData, montant: value };

            // Synchronisation : Si la liste n'a qu'un seul élément, on met à jour son montant.
            if (updatedData.bill_payments.length === 1) {
                updatedData.bill_payments[0].montant = value;
            }
            if (updatedData.bill_beneficiaries.length === 1) {
                updatedData.bill_beneficiaries[0].amount_owed = value;
            }

            // Si le montant devient vide ou zéro, s'assurer que les listes ne contiennent qu'un seul élément vide (pour la saisie)
            if (value === "" || parseFloat(value) === 0) {
                if (updatedData.bill_payments.length === 0) {
                    updatedData.bill_payments = [{ mode: 'Virement', montant: '', payer_person_id: prevData.person_id }];
                }
                if (updatedData.bill_beneficiaries.length === 0) {
                    updatedData.bill_beneficiaries = [{ amount_owed: '', beneficiary_person_id: prevData.person_id }];
                }
            }

            return updatedData;
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        // 💡 CORRECTION : Convertir en nombre si c'est un ID
        let finalValue = value;
        if (name === 'person_id' || name === 'account_id' || name === 'categorie_id') {
            // Si la valeur est vide, elle reste une chaîne vide; sinon, elle devient un nombre
            finalValue = value ? Number(value) : "";
        }

        setFormData((prevData) => ({ ...prevData, [name]: finalValue }));
    };

    // LOGIQUE Bill Payments (Qui a payé) -------------------
    const addBillPayment = () => {
        setFormData((prevData) => ({
            ...prevData,
            bill_payments: [...prevData.bill_payments, {
                mode: "Virement",
                montant: "",
                payer_person_id: String(selectedPerson?.id || 0),
                // is_debt_creditor retiré
            }],
        }));
    };

    const removeBillPayment = (index) => {
        setFormData((prevData) => ({
            ...prevData,
            bill_payments: prevData.bill_payments.filter((_, i) => i !== index),
        }));
    };

    const updateBillPayment = (index, field, value) => {
        const updated = [...formData.bill_payments];
        if (field === 'payer_person_id') {
            // ✅ Assurez-vous que l'ID est stocké comme Number
            updated[index][field] = Number(value);
        } else {
            updated[index][field] = value;
        }
        setFormData({ ...formData, bill_payments: updated });
    };

    const sumBillPayments = Math.round(formData.bill_payments.reduce(
        (sum, p) => sum + Number(p.montant || 0),
        0
    ) * 100) / 100;

    // LOGIQUE Bill Beneficiaries (Pour qui a été payé) -------------------
    const addBillBeneficiary = () => {
        setFormData((prevData) => ({
            ...prevData,
            bill_beneficiaries: [...prevData.bill_beneficiaries, {
                beneficiary_person_id: String(selectedPerson?.id || 0),
                amount_owed: "",
                // is_debt_debtor retiré
            }],
        }));
    };

    const removeBillBeneficiary = (index) => {
        setFormData((prevData) => ({
            ...prevData,
            bill_beneficiaries: prevData.bill_beneficiaries.filter((_, i) => i !== index),
        }));
    };

    const updateBillBeneficiary = (index, field, value) => {
        const updated = [...formData.bill_beneficiaries];
        if (field === 'beneficiary_person_id') {
            // ✅ Assurez-vous que l'ID est stocké comme Number
            updated[index][field] = Number(value);
        } else {
            updated[index][field] = value;
        }
        setFormData({ ...formData, bill_beneficiaries: updated });
    };

    const sumBillBeneficiaries = Math.round(formData.bill_beneficiaries.reduce(
        (sum, b) => sum + Number(b.amount_owed || 0),
        0
    ) * 100) / 100;

    const filteredAccounts = accounts.filter(
        (acc) => acc.personId === formData.person_id
    );

    // La logique de sauvegarde est la même, mais les données envoyées sont simplifiées
    const handleSave = async (e) => {
        e.preventDefault();

        const totalFacture = Math.round(Number(formData.montant || 0) * 100) / 100;

        // 1. Vérification des sommes 
        if (sumBillPayments !== totalFacture) {
            alert(`La somme des règlements (${sumBillPayments} €) ne correspond pas au montant total (${totalFacture} €).`);
            return;
        }
        if (sumBillBeneficiaries !== totalFacture) {
            alert(`La somme des bénéficiaires (${sumBillBeneficiaries} €) ne correspond pas au montant total (${totalFacture} €).`);
            return;
        }

        const isEditing = !!editingFacture?.id;
        const method = isEditing ? "put" : "post";
        const urlPath = isEditing
            ? `/factures/${editingFacture.id}`
            : `/factures`;

        const dataToSave = {
            // Champs de facture standard
            ...formData,
            person_id: Number(formData.person_id),
            account_id: Number(formData.account_id),

            // On envoie les nouvelles listes simplifiées
            bill_payments: formData.bill_payments.map(p => ({
                ...p,
                payer_person_id: Number(p.payer_person_id), // Conversion en Number
            })),

            bill_beneficiaries: formData.bill_beneficiaries.map(b => ({
                ...b,
                beneficiary_person_id: Number(b.beneficiary_person_id), // Conversion en Number
            })),

            // On garde l'ancienne paiements pour la rétrocompatibilité du BE
            paiements: formData.bill_payments.map(p => ({ mode: p.mode, montant: p.montant })),
        };

        try {
            const response = await authFetch[method](urlPath, dataToSave);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(
                    errorData.error || "Erreur lors de la sauvegarde de la facture."
                );
            }

            refreshDashboardData();
            onSave();
            onCancel();
        } catch (error) {
            console.error("Erreur de sauvegarde :", error);
            alert(error.message);
        }
    };

    const handleDelete = async () => {
        // ... (Logique de suppression inchangée)
        if (!editingFacture || !editingFacture.id) return;
        if (
            window.confirm(
                "Êtes-vous sûr de vouloir supprimer cette facture et tous ses éléments associés (y compris les dettes/créances)?"
            )
        ) {
            try {
                const response = await authFetch.delete(
                    `factures/${editingFacture.id}`
                );
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || "Erreur lors de la suppression de la facture.");
                }

                if (onDeleteCallback) {
                    onDeleteCallback(); 
                } else {
                    refreshDashboardData(); 
                }
                onCancel();
            } catch (error) {
                console.error("Erreur de suppression :", error);
                alert(error.message);
            }
        }
    };

    if (!show) {
        return null;
    }

    return (
        <div style={styles.modalOverlay}>
            <div style={styles.modal}>
                <h3>{editingFacture ? "Modifier facture" : "Nouvelle facture"}</h3>

                <form onSubmit={handleSave} style={styles.form}>
                    {/* Champs standard (inchangés) */}
                    <input
                        type="date" name="date" value={formData.date} onChange={handleChange} style={styles.input}
                    />

                    {/* Catégorie / Création rapide (inchangé) */}
                    {showCategoryForm ? (
                        <div style={styles.categoryFormContainer}>
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                <input // INPUT NOM
                                    type="text" 
                                    placeholder="Nom de la nouvelle catégorie" 
                                    value={newCategoryName} 
                                    onChange={(e) => setNewCategoryName(e.target.value)} 
                                    style={{ ...styles.input, flexGrow: 1 }}
                                    required 
                                />
                                
                                {/* 💡 SÉLECTEUR DE TYPE DE FLUX */}
                                <select
                                    value={newCategoryType}
                                    onChange={(e) => setNewCategoryType(e.target.value)}
                                    style={{ ...styles.input, width: '120px', flexShrink: 0 }} 
                                    required
                                >
                                    {FLOW_TYPES.map(type => ( // 💡 Utilisation de la liste centralisée
                                        <option key={type.code} value={type.code}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div style={styles.smallActions}>
                                <button type="button" onClick={() => { setShowCategoryForm(false); setNewCategoryName(''); setNewCategoryType('D') }} style={styles.cancelSmall}>Annuler</button>
                                <button type="button" onClick={handleCreateCategory} style={styles.addSmall}>Créer</button>
                            </div>
                        </div>
                    ) : (
                        <div style={styles.categorySelectorContainer}>
                            <select name="categorie_id" value={formData.categorie_id || ""} onChange={handleChange} style={{ ...styles.input, flexGrow: 1 }} required>
                                <option value="">Choisir une catégorie</option>
                                {visibleCategories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                            </select>
                            <button type="button" onClick={() => setShowCategoryForm(true)} style={styles.actionSmall} title="Créer une nouvelle catégorie">➕</button>
                        </div>
                    )}

                    {/* SÉLECTION DU DÉBITEUR PRINCIPAL (Ancienne logique de personne) */}
                    <select
                        name="person_id"
                        value={formData.person_id || ""}
                        onChange={(e) => {
                            const newPersonId = parseInt(e.target.value);
                            setFormData((prevData) => ({
                                ...prevData,
                                person_id: newPersonId,
                                account_id: accounts.find((acc) => acc.personId === newPersonId)?.id || "",
                            }));
                        }}
                        style={styles.input}
                        required
                    >
                        <option value="">Choisir la personne (Contexte principal)</option>
                        {persons.filter(p => p.is_managed).map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                    </select>

                    <select name="account_id" value={formData.account_id || ""} onChange={handleChange} style={styles.input} required>
                        <option value="">Choisir un compte</option>
                        {filteredAccounts.map((c) => (<option key={c.id} value={String(c.id)}>{c.name}</option>))}
                    </select>

                    <select name="statut" value={formData.statut || "Non payé"} onChange={handleChange} style={styles.input}>
                        <option value="Non payé">Non payé</option>
                        <option value="Payé">Payé</option>
                    </select>

                    {/* MONTANT TOTAL */}
                    <input
                        type="number" step="0.01" placeholder="Montant total de la facture" name="montant" value={formData.montant || ""} onChange={handleMontantChange} style={styles.input}
                    />

                    <input
                        type="text" placeholder="Description (ex: Carrefour)" name="description" value={formData.description || ""} onChange={handleChange} style={styles.input}
                    />

                    {/* ========================================================= */}
                    {/* ✅ ENCADRÉ SIMPLIFIÉ : Qui a payé (Créditeur) */}
                    {/* ========================================================= */}
                    <div style={styles.paymentSection}>
                        <h4 style={styles.sectionTitle}>
                            💸 Règlements (Qui a payé ?)
                            <span style={styles.totalSum(sumBillPayments, Number(formData.montant))}>
                                Total : {sumBillPayments.toFixed(2)} € / {Number(formData.montant || 0).toFixed(2)} €
                            </span>
                        </h4>

                        {formData.bill_payments?.map((p, index) => (
                            <div key={index} style={styles.paymentRow}>
                                {/* Mode de règlement */}
                                <select value={p.mode || "CB"} onChange={(e) => updateBillPayment(index, "mode", e.target.value)} style={{ ...styles.input, flex: 2 }}>
                                    {PAYMENTS_MOD.map(m => (<option key={m} value={m}>{m}</option>))}
                                </select>

                                {/* Montant payé */}
                                <input
                                    type="number" step="0.01" placeholder="Montant" value={p.montant || ""} onChange={(e) => updateBillPayment(index, "montant", e.target.value)} style={{ ...styles.input, flex: 1.5 }}
                                />

                                {/* Personne qui a payé (Créditeur) */}
                                <select value={p.payer_person_id || ""} onChange={(e) => updateBillPayment(index, "payer_person_id", e.target.value)} style={{ ...styles.input, flex: 2.5 }} required>
                                    <option value="">Payé par...</option>
                                    {allPersons.map((person) => (<option key={person.id} value={String(person.id)}>{person.name}</option>))}
                                </select>

                                <button type="button" style={styles.deleteSmall} onClick={() => removeBillPayment(index)} disabled={formData.bill_payments.length === 1}>❌</button>
                            </div>
                        ))}
                            <button type="button" style={styles.addSmall} onClick={addBillPayment} disabled={Number(sumBillPayments) >= Number(formData.montant) && formData.montant !== ""}>
                                ➕ Ajouter un règlement
                            </button>            
                    </div>

                    {/* ========================================================= */}
                    {/* ✅ ENCADRÉ SIMPLIFIÉ : Pour qui a été payé (Débiteur) */}
                    {/* ========================================================= */}
                    <div style={styles.paymentSection}>
                        <h4 style={styles.sectionTitle}>
                            🎯 Bénéficiaires (Pour qui a été payé ?)
                            <span style={styles.totalSum(sumBillBeneficiaries, Number(formData.montant))}>
                                Total : {sumBillBeneficiaries.toFixed(2)} € / {Number(formData.montant || 0).toFixed(2)} €
                            </span>
                        </h4>

                        {formData.bill_beneficiaries?.map((b, index) => (
                            <div key={index} style={styles.paymentRow}>

                                {/* Personne Bénéficiaire (Débiteur) */}
                                <select value={b.beneficiary_person_id || ""} onChange={(e) => updateBillBeneficiary(index, "beneficiary_person_id", e.target.value)} style={{ ...styles.input, flex: 2.5 }} required>
                                    <option value="">Bénéficiaire...</option>
                                    {allPersons.map((person) => (<option key={person.id} value={String(person.id)}>{person.name}</option>))}
                                </select>

                                {/* Montant dû */}
                                <input
                                    type="number" step="0.01" placeholder="Montant Dû" value={b.amount_owed || ""} onChange={(e) => updateBillBeneficiary(index, "amount_owed", e.target.value)} style={{ ...styles.input, flex: 1.5 }}
                                />

                                <button type="button" style={styles.deleteSmall} onClick={() => removeBillBeneficiary(index)} disabled={formData.bill_beneficiaries.length === 1}>❌</button>
                            </div>
                        ))}
                            <button type="button" style={styles.addSmall} onClick={addBillBeneficiary} disabled={Number(sumBillBeneficiaries) >= Number(formData.montant) && formData.montant !== ""}>
                                ➕ Ajouter un bénéficiaire
                            </button>
                    </div>
                    {/* FIN NOUVEAUX ENCADRÉS */}

                    <div style={styles.modalActions}>
                        {editingFacture && (
                            <button type="button" style={styles.delete} onClick={handleDelete}>Supprimer</button>
                        )}
                        <button type="button" style={styles.cancel} onClick={onCancel}>Annuler</button>
                        <button
                            type="submit"
                            style={styles.save}
                            disabled={sumBillPayments !== Number(formData.montant) || sumBillBeneficiaries !== Number(formData.montant)}
                        >
                            {editingFacture ? "Enregistrer" : "Ajouter"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// Styles (Réorganisé pour la clarté mais avec les valeurs d'origine)
const styles = {
    modalOverlay: {
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center",
        zIndex: 1000,
    },
    modal: {
        background: "#fff", padding: "20px", borderRadius: "8px", width: "800px", maxHeight: '90vh', overflowY: 'auto'
    },
    form: { display: "flex", flexDirection: "column", gap: "10px" },
    input: { padding: "10px", borderRadius: "4px", border: "1px solid #ccc", },
    modalActions: { display: "flex", justifyContent: "flex-end", marginTop: "10px", gap: "10px", },
    save: { background: "#28a745", color: "#fff", border: "none", padding: "10px 15px", borderRadius: 4, cursor: 'pointer' },
    cancel: { background: "#6c757d", color: "#fff", border: "none", padding: "10px 15px", borderRadius: 4, cursor: 'pointer' },
    delete: { background: "#584749ff", color: "#fff", border: "none", padding: "10px 15px", borderRadius: 4, marginRight: "auto", cursor: 'pointer' },
    deleteSmall: { background: "#dc3545", color: "#fff", border: "none", padding: "5px 8px", borderRadius: 4, cursor: "pointer", flexShrink: 0 },
    addSmall: { background: "#007bff", color: "#fff", border: "none", padding: "5px 10px", borderRadius: 4, cursor: "pointer", marginTop: 0, alignSelf: 'flex-end', flexShrink: 0 },
    actionSmall: {
        background: '#ff9800', color: '#fff', border: 'none', padding: '10px 10px', borderRadius: '4px', cursor: 'pointer',
        width: '40px', height: '40px', alignSelf: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0,
    },
    cancelSmall: { background: '#6c757d', color: '#fff', border: 'none', padding: '5px 10px', borderRadius: 4, cursor: 'pointer', marginTop: 0 },
    warning: { color: '#856404', backgroundColor: '#fff3cd', border: '1px solid #ffeeba', padding: '10px', borderRadius: '4px', fontSize: '0.9em' },
    categoryFormContainer: { padding: '10px', border: '1px solid #ccc', borderRadius: '4px', gap: '10px' },
    categorySelectorContainer: { display: 'flex', gap: '5px', alignItems: 'center' },
    smallActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
    // Les styles de section peuvent être conservés car ils ne définissent pas le background principal en sombre
    paymentSection: { border: '1px solid #007bff', padding: '15px', borderRadius: '8px', marginTop: '15px', backgroundColor: '#f5faff' },
    sectionTitle: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #ccc', paddingBottom: '5px', marginBottom: '10px', fontSize: '1.1em' },
    paymentRow: { display: "flex", gap: "10px", marginBottom: "5px", alignItems: 'center' },
    totalSum: (current, expected) => {
        const isOk = Math.abs(current - Number(expected)) < 0.001;
        return {
            fontSize: '0.9em',
            fontWeight: 'normal',
            color: isOk ? 'green' : 'red',
        };
    },
};